import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';
import { Event } from '../../models/event.model';
import * as eventActions from '../actions/event.actions';

export interface EventState extends EntityState<Event> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  eventStatsLoading: boolean;
  error: AppExceptionModel | undefined;
}

export const eventAdapter: EntityAdapter<Event> = createEntityAdapter<Event>();

export const initialState: EventState = eventAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  eventStatsLoading: false,
  error: undefined,
});

export const eventReducer = createReducer(
  initialState,
  on(eventActions.getEvents, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(eventActions.getEvent, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(eventActions.loadEvents, (state, { events }) => {
    if (events?.length > 0) {
      return eventAdapter.upsertMany(events, {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      });
    } else {
      return {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      };
    }
  }),
  on(eventActions.loadEvent, (state, { event }) =>
    eventAdapter.upsertOne(event, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(eventActions.getEventsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(eventActions.create, (state, { event }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(eventActions.createSuccess, (state, { event }) =>
    eventAdapter.addOne(event, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(eventActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(eventActions.update, (state, { event }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(eventActions.updateSuccess, (state, { event }) =>
    eventAdapter.upsertOne(event, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(eventActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(eventActions.updateStatus, (state, { event }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(eventActions.updateStatusSuccess, (state, { event }) =>
    eventAdapter.upsertOne(event, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(eventActions.updateStatusFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(eventActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(eventActions.deleteSuccess, (state, { id }) =>
    eventAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(eventActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(eventActions.getEventStats, (state) => ({
    ...state,
    eventStatsLoading: true,
  })),
  on(eventActions.getEventStatsDone, (state) => ({
    ...state,
    eventStatsLoading: false,
  })),
  on(eventActions.getEventStatsFailure, (state) => ({
    ...state,
    eventStatsLoading: false,
  })),
  on(eventActions.clear, (state) => initialState),
);
